import { useEffect, useRef, useCallback, useState, useMemo } from "react";
import * as fabric from "fabric";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { useCanvasRegistry } from "@/stores/canvasRegistry";
import { cn } from "@/lib/utils";
import type { Point2D, EditorTool, StructuralElementType } from "@byk/shared";
import { getSnapGuideColor, getSnapGuideDash } from "@/services/snapping";
import {
  detectWallHit,
  validateElementPlacement,
  ELEMENT_DEFAULTS,
} from "@/services/wallGeometry";

// Cursor styles based on active tool
const TOOL_CURSORS: Record<EditorTool, string> = {
  select: "cursor-default",
  wall: "cursor-crosshair",
  window: "cursor-crosshair",
  door: "cursor-crosshair",
  measure: "cursor-crosshair",
  place: "cursor-copy",
  move: "cursor-move",
  rotate: "cursor-alias",
};

// Scale: 1cm = 2px for good visibility
const SCALE = 2;
const GRID_COLOR = "#e5e7eb";
const WALL_COLOR = "#374151";
const VOLUME_COLOR = "rgba(59, 130, 246, 0.5)";
const VOLUME_STROKE = "#2563eb";
const ELEMENT_WINDOW_COLOR = "#93c5fd";
const ELEMENT_DOOR_COLOR = "#fbbf24";
const ROOM_PADDING = 50; // Padding around room in pixels

// WCAG 2.4.11 Focus Appearance - High contrast focus ring
const FOCUS_RING_COLOR = "#2563EB"; // Blue-600, 4.5:1 contrast on white
const FOCUS_RING_WIDTH = 4; // 4px thickness for visibility
const FOCUS_SHADOW_COLOR = "rgba(37, 99, 235, 0.5)";
const FOCUS_SHADOW_BLUR = 12;

// Ghost element preview state for door/window placement
interface ElementPreview {
  wallId: string;
  offset: number;
  type: StructuralElementType;
  width: number;
  isValid: boolean;
  reason?: string;
}

export const FloorPlanCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<fabric.Canvas | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [elementPreview, setElementPreview] = useState<ElementPreview | null>(null);

  const {
    room,
    walls,
    elements,
    volumes,
    selectedId,
    activeTool,
    gridSize,
    snapToGrid,
    placementMode,
    pendingPlacement,
    snapGuideLines,
    isDragging,
  } = useFloorPlanStore();

  const {
    addWall,
    addVolume,
    addElement,
    select,
    setDrawing,
    setTool,
    getProductById,
    setDraggedProduct,
    updateOccupancy,
    isPlacementValid,
    placementPreview,
    setPlacementPreview,
    clearPlacementPreview,
    confirmClickPlacement,
    calculateSnapPosition,
    setDragging,
    clearSnapGuides,
    // BYK-3: Undo/Redo
    undo,
    redo,
    canUndo,
    canRedo,
    removeVolume,
  } = useFloorPlanStore();

  // BYK-177: Canvas registry for export
  const registerFabricCanvas = useCanvasRegistry((s) => s.registerFabricCanvas);

  // Fit room to view - center the room in the viewport
  const fitToView = useCallback(() => {
    if (!fabricRef.current || !containerRef.current) return;

    const canvas = fabricRef.current;
    const container = containerRef.current;

    // Get CSS display dimensions (what user sees)
    const displayWidth = container.clientWidth;
    const displayHeight = container.clientHeight;

    // Don't proceed if no size
    if (displayWidth <= 0 || displayHeight <= 0) return;

    // Room dimensions in pixels at scale
    const roomWidthPx = room.width * SCALE;
    const roomHeightPx = room.depth * SCALE;

    // Calculate zoom to fit room with padding
    const totalWidth = roomWidthPx + ROOM_PADDING * 2;
    const totalHeight = roomHeightPx + ROOM_PADDING * 2;
    const zoomX = displayWidth / totalWidth;
    const zoomY = displayHeight / totalHeight;
    const newZoom = Math.min(zoomX, zoomY, 1.5); // Cap at 1.5x

    // Room center in canvas coordinates
    const roomCenterX = roomWidthPx / 2;
    const roomCenterY = roomHeightPx / 2;

    // Screen center
    const screenCenterX = displayWidth / 2;
    const screenCenterY = displayHeight / 2;

    // To center the room:
    // We want roomCenter (in canvas coords) to appear at screenCenter
    // screen = canvas * zoom + offset
    // screenCenterX = roomCenterX * zoom + offsetX
    // offsetX = screenCenterX - roomCenterX * zoom
    const offsetX = screenCenterX - roomCenterX * newZoom;
    const offsetY = screenCenterY - roomCenterY * newZoom;

    // Apply viewport transform: [scaleX, skewY, skewX, scaleY, translateX, translateY]
    const vpt: [number, number, number, number, number, number] = [newZoom, 0, 0, newZoom, offsetX, offsetY];
    canvas.setViewportTransform(vpt);

    setZoom(newZoom);
    canvas.requestRenderAll();
  }, [room]);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    fabricRef.current = new fabric.Canvas(canvasRef.current, {
      width,
      height,
      backgroundColor: "#fafafa",
      selection: activeTool === "select",
    });

    const canvas = fabricRef.current;

    // BYK-177: Register canvas for export
    registerFabricCanvas(canvas);

    // Enable pan with Alt+drag, middle mouse, or spacebar+drag (Figma-style)
    let isPanning = false;
    let isSpacePressed = false;
    let lastPosX = 0;
    let lastPosY = 0;

    // Track spacebar state for spacebar+drag panning
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" && !isSpacePressed) {
        isSpacePressed = true;
        canvas.defaultCursor = "grab";
        // Prevent page scroll when spacebar is pressed
        if (document.activeElement === containerRef.current ||
            document.activeElement?.closest('[data-fabric-canvas]')) {
          e.preventDefault();
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        isSpacePressed = false;
        if (!isPanning) {
          canvas.defaultCursor = activeTool === "wall" ? "crosshair" : "default";
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    canvas.on("mouse:down", (opt) => {
      const evt = opt.e as MouseEvent;
      // Pan with Alt+drag, middle mouse, or spacebar+drag
      if (evt.altKey || evt.button === 1 || isSpacePressed) {
        isPanning = true;
        canvas.selection = false;
        lastPosX = evt.clientX;
        lastPosY = evt.clientY;
        canvas.defaultCursor = "grabbing";
      }
    });

    canvas.on("mouse:move", (opt) => {
      if (isPanning) {
        const evt = opt.e as MouseEvent;
        const vpt = canvas.viewportTransform;
        if (vpt) {
          vpt[4] += evt.clientX - lastPosX;
          vpt[5] += evt.clientY - lastPosY;
          canvas.requestRenderAll();
          lastPosX = evt.clientX;
          lastPosY = evt.clientY;
        }
      }
    });

    canvas.on("mouse:up", () => {
      isPanning = false;
      canvas.selection = activeTool === "select";
      canvas.defaultCursor = activeTool === "wall" ? "crosshair" : "default";
    });

    // Persist volume position after drag/transform (fixes drag not persisting)
    canvas.on("object:modified", (e) => {
      const obj = e.target as fabric.FabricObject & { isVolume?: boolean; volumeId?: string };
      if (obj && obj.isVolume && obj.volumeId) {
        // Volumes use originX/Y: 'center', so left/top is the center position
        const newX = (obj.left ?? 0) / SCALE;
        const newY = (obj.top ?? 0) / SCALE;
        // Get current volumes from store (not stale closure)
        const currentVolumes = useFloorPlanStore.getState().volumes;
        const existingVolume = currentVolumes.find((v) => v.id === obj.volumeId);
        const z = existingVolume?.position.z ?? 0;
        // Use store action directly to avoid stale closure
        useFloorPlanStore.getState().moveVolume(obj.volumeId, { x: newX, y: newY, z });
      }
    });

    // Zoom with mouse wheel
    canvas.on("mouse:wheel", (opt) => {
      const evt = opt.e as WheelEvent;
      evt.preventDefault();
      evt.stopPropagation();

      const delta = evt.deltaY;
      let newZoom = canvas.getZoom() * (delta > 0 ? 0.9 : 1.1);

      // Limit zoom range
      newZoom = Math.min(Math.max(newZoom, 0.1), 3);

      // Zoom to mouse point
      const point = new fabric.Point(evt.offsetX, evt.offsetY);
      canvas.zoomToPoint(point, newZoom);
      setZoom(newZoom);
    });

    // Handle resize using ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver((entries) => {
      if (!fabricRef.current) return;

      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          // Update canvas dimensions to match container
          fabricRef.current.setDimensions({ width, height });
          // Re-center room after resize
          fitToView();
        }
      }
    });

    resizeObserver.observe(container);

    // Initial fit to view - use requestAnimationFrame for proper timing
    requestAnimationFrame(() => {
      fitToView();
    });

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      resizeObserver.disconnect();
      // BYK-177: Unregister canvas before dispose
      registerFabricCanvas(null);
      fabricRef.current?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Canvas init on room change; activeTool/fitToView/registerFabricCanvas are stable refs
  }, [room.width, room.depth]);

  // Re-fit when room changes
  useEffect(() => {
    fitToView();
  }, [room.width, room.depth, fitToView]);

  // Update occupancy grid when walls/volumes change
  useEffect(() => {
    updateOccupancy();
  }, [walls, volumes, updateOccupancy]);

  // Draw grid
  const drawGrid = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing grid lines
    canvas.getObjects("line").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isGrid?: boolean }).isGrid) {
        canvas.remove(obj);
      }
    });

    const gridSpacing = gridSize * SCALE;
    const width = room.width * SCALE;
    const height = room.depth * SCALE;

    // Vertical lines
    for (let x = 0; x <= width; x += gridSpacing) {
      const line = new fabric.Line([x, 0, x, height], {
        stroke: GRID_COLOR,
        strokeWidth: 1,
        selectable: false,
        evented: false,
      });
      (line as fabric.Line & { isGrid?: boolean }).isGrid = true;
      canvas.add(line);
      canvas.sendObjectToBack(line);
    }

    // Horizontal lines
    for (let y = 0; y <= height; y += gridSpacing) {
      const line = new fabric.Line([0, y, width, y], {
        stroke: GRID_COLOR,
        strokeWidth: 1,
        selectable: false,
        evented: false,
      });
      (line as fabric.Line & { isGrid?: boolean }).isGrid = true;
      canvas.add(line);
      canvas.sendObjectToBack(line);
    }

    canvas.renderAll();
  }, [room, gridSize]);

  // Draw room boundary
  const drawRoomBoundary = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing boundary and debug elements
    canvas.getObjects("rect").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isBoundary?: boolean }).isBoundary) {
        canvas.remove(obj);
      }
    });
    canvas.getObjects("circle").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isDebug?: boolean }).isDebug) {
        canvas.remove(obj);
      }
    });

    const boundary = new fabric.Rect({
      left: 0,
      top: 0,
      width: room.width * SCALE,
      height: room.depth * SCALE,
      fill: "white",
      stroke: "#6b7280",
      strokeWidth: 3,
      strokeDashArray: [10, 5],
      selectable: false,
      evented: false,
      // Fabric.js v7 defaults to 'center' origin - explicitly set to 'left'/'top'
      originX: 'left',
      originY: 'top',
    });
    (boundary as fabric.Rect & { isBoundary?: boolean }).isBoundary = true;
    canvas.add(boundary);
    canvas.sendObjectToBack(boundary);

    // Debug dots removed for clean view
  }, [room]);

  // Draw walls
  const drawWalls = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing wall objects
    canvas.getObjects("line").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isWall?: boolean }).isWall) {
        canvas.remove(obj);
      }
    });

    walls.forEach((wall) => {
      const isSelected = wall.id === selectedId;

      const line = new fabric.Line(
        [
          wall.start.x * SCALE,
          wall.start.y * SCALE,
          wall.end.x * SCALE,
          wall.end.y * SCALE,
        ],
        {
          stroke: isSelected ? FOCUS_RING_COLOR : WALL_COLOR,
          strokeWidth: isSelected ? wall.thickness * SCALE + FOCUS_RING_WIDTH : wall.thickness * SCALE,
          strokeLineCap: "round",
          selectable: activeTool === "select",
          hasControls: false,
          hasBorders: true,
          // WCAG 2.4.11: Focus shadow for walls
          shadow: isSelected
            ? new fabric.Shadow({
                color: FOCUS_SHADOW_COLOR,
                blur: FOCUS_SHADOW_BLUR,
                offsetX: 0,
                offsetY: 0,
              })
            : undefined,
        }
      );
      (line as fabric.Line & { isWall?: boolean; wallId?: string }).isWall = true;
      (line as fabric.Line & { wallId?: string }).wallId = wall.id;

      // Selection handler
      line.on("selected", () => select(wall.id));

      canvas.add(line);
    });

    canvas.renderAll();
  }, [walls, selectedId, activeTool, select]);

  // Draw structural elements (windows, doors)
  const drawElements = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing element objects
    canvas.getObjects("rect").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isElement?: boolean }).isElement) {
        canvas.remove(obj);
      }
    });

    elements.forEach((element) => {
      const wall = walls.find((w) => w.id === element.wallId);
      if (!wall) return;

      // Calculate position along wall
      const wallLength = Math.hypot(
        wall.end.x - wall.start.x,
        wall.end.y - wall.start.y
      );
      const ratio = element.offset / wallLength;
      const x = wall.start.x + (wall.end.x - wall.start.x) * ratio;
      const y = wall.start.y + (wall.end.y - wall.start.y) * ratio;

      const angle =
        Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) *
        (180 / Math.PI);

      const color =
        element.type === "window" ? ELEMENT_WINDOW_COLOR : ELEMENT_DOOR_COLOR;
      const isSelected = element.id === selectedId;

      const rect = new fabric.Rect({
        left: x * SCALE,
        top: y * SCALE,
        width: element.width * SCALE,
        height: wall.thickness * SCALE,
        fill: color,
        stroke: isSelected ? FOCUS_RING_COLOR : "#666",
        strokeWidth: isSelected ? FOCUS_RING_WIDTH : 1,
        angle,
        originX: "center",
        originY: "center",
        selectable: activeTool === "select",
        hasControls: false,
        // WCAG 2.4.11: Focus shadow for elements
        shadow: isSelected
          ? new fabric.Shadow({
              color: FOCUS_SHADOW_COLOR,
              blur: FOCUS_SHADOW_BLUR,
              offsetX: 0,
              offsetY: 0,
            })
          : undefined,
      });
      (rect as fabric.Rect & { isElement?: boolean; elementId?: string }).isElement = true;
      (rect as fabric.Rect & { elementId?: string }).elementId = element.id;

      rect.on("selected", () => select(element.id));

      canvas.add(rect);
    });

    canvas.renderAll();
  }, [elements, walls, selectedId, activeTool, select]);

  // Draw ghost element preview for door/window placement
  const drawElementPreview = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing preview objects
    canvas.getObjects("rect").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isElementPreview?: boolean }).isElementPreview) {
        canvas.remove(obj);
      }
    });
    // Remove preview arc (for doors)
    canvas.getObjects("path").forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isElementPreview?: boolean }).isElementPreview) {
        canvas.remove(obj);
      }
    });

    if (!elementPreview) {
      canvas.renderAll();
      return;
    }

    const wall = walls.find((w) => w.id === elementPreview.wallId);
    if (!wall) return;

    // Calculate position along wall
    const wallLength = Math.hypot(
      wall.end.x - wall.start.x,
      wall.end.y - wall.start.y
    );
    const ratio = elementPreview.offset / wallLength;
    const x = wall.start.x + (wall.end.x - wall.start.x) * ratio;
    const y = wall.start.y + (wall.end.y - wall.start.y) * ratio;

    const angle =
      Math.atan2(wall.end.y - wall.start.y, wall.end.x - wall.start.x) *
      (180 / Math.PI);

    // Ghost colors - green for valid, red for invalid
    const fillColor = elementPreview.isValid
      ? "rgba(34, 197, 94, 0.4)" // green-500 with 40% opacity
      : "rgba(239, 68, 68, 0.4)"; // red-500 with 40% opacity
    const strokeColor = elementPreview.isValid
      ? "#16a34a" // green-600
      : "#dc2626"; // red-600

    const rect = new fabric.Rect({
      left: x * SCALE,
      top: y * SCALE,
      width: elementPreview.width * SCALE,
      height: wall.thickness * SCALE,
      fill: fillColor,
      stroke: strokeColor,
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      angle,
      originX: "center",
      originY: "center",
      selectable: false,
      evented: false,
    });
    (rect as fabric.Rect & { isElementPreview?: boolean }).isElementPreview = true;
    canvas.add(rect);

    // For doors, add a swing arc indicator
    if (elementPreview.type === "door" && elementPreview.isValid) {
      const doorWidth = elementPreview.width * SCALE;
      const angleRad = angle * (Math.PI / 180);

      // Door swing arc (90 degrees)
      // Arc center is at the hinge side of the door
      const hingeX = x * SCALE - (doorWidth / 2) * Math.cos(angleRad);
      const hingeY = y * SCALE - (doorWidth / 2) * Math.sin(angleRad);

      // Create a quarter-circle path for the door swing
      const arcPath = new fabric.Path(
        `M ${hingeX} ${hingeY}
         L ${hingeX + doorWidth * Math.cos(angleRad)} ${hingeY + doorWidth * Math.sin(angleRad)}
         A ${doorWidth} ${doorWidth} 0 0 1 ${hingeX + doorWidth * Math.cos(angleRad + Math.PI / 2)} ${hingeY + doorWidth * Math.sin(angleRad + Math.PI / 2)}
         Z`,
        {
          fill: "rgba(251, 191, 36, 0.2)", // amber-400 with low opacity
          stroke: "#f59e0b", // amber-500
          strokeWidth: 1,
          strokeDashArray: [3, 3],
          selectable: false,
          evented: false,
        }
      );
      (arcPath as fabric.Path & { isElementPreview?: boolean }).isElementPreview = true;
      canvas.add(arcPath);
    }

    canvas.renderAll();
  }, [elementPreview, walls]);

  // Draw snap guide lines (BYK-32)
  const drawSnapGuides = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing snap guide objects
    canvas.getObjects().forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isSnapGuide?: boolean }).isSnapGuide) {
        canvas.remove(obj);
      }
    });

    // Draw new snap guide lines
    for (const guide of snapGuideLines) {
      const color = getSnapGuideColor(guide.type, guide.isActive);
      const dashArray = getSnapGuideDash(guide.type);

      const line = new fabric.Line(
        [
          guide.start.x * SCALE,
          guide.start.y * SCALE,
          guide.end.x * SCALE,
          guide.end.y * SCALE,
        ],
        {
          stroke: color,
          strokeWidth: guide.isActive ? 2 : 1,
          strokeDashArray: dashArray.length > 0 ? dashArray : undefined,
          selectable: false,
          evented: false,
          opacity: guide.isActive ? 1 : 0.5,
        }
      );
      (line as fabric.Line & { isSnapGuide?: boolean }).isSnapGuide = true;
      canvas.add(line);
      canvas.bringObjectToFront(line);

      // Add snap point indicator for active snaps
      if (guide.isActive && (guide.type === "corner" || guide.type === "wall")) {
        const point = new fabric.Circle({
          left: guide.end.x * SCALE,
          top: guide.end.y * SCALE,
          radius: 6,
          fill: color,
          stroke: "#fff",
          strokeWidth: 2,
          originX: "center",
          originY: "center",
          selectable: false,
          evented: false,
        });
        (point as fabric.Circle & { isSnapGuide?: boolean }).isSnapGuide = true;
        canvas.add(point);
        canvas.bringObjectToFront(point);
      }
    }

    canvas.renderAll();
  }, [snapGuideLines]);

  // Draw placed volumes (top-down view of products)
  const drawVolumes = useCallback(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    // Remove existing volume objects (rects and text)
    canvas.getObjects().forEach((obj: fabric.FabricObject) => {
      if ((obj as fabric.FabricObject & { isVolume?: boolean }).isVolume) {
        canvas.remove(obj);
      }
    });

    volumes.forEach((vol) => {
      const product = getProductById(vol.productId);
      if (!product?.dimensions) return;

      const isSelected = vol.id === selectedId;
      const width = product.dimensions.width * SCALE;
      const height = product.dimensions.depth * SCALE;

      // Create rect at origin (will be positioned by group)
      const rect = new fabric.Rect({
        left: -width / 2,
        top: -height / 2,
        width,
        height,
        fill: VOLUME_COLOR,
        stroke: isSelected ? FOCUS_RING_COLOR : VOLUME_STROKE,
        strokeWidth: isSelected ? FOCUS_RING_WIDTH : 1,
        originX: "left",
        originY: "top",
        // Focus shadow for accessibility
        shadow: isSelected
          ? new fabric.Shadow({
              color: FOCUS_SHADOW_COLOR,
              blur: FOCUS_SHADOW_BLUR,
              offsetX: 0,
              offsetY: 0,
            })
          : undefined,
      });

      // Create label at center of rect (origin of group)
      const label = new fabric.Text(product.name.substring(0, 15), {
        left: 0,
        top: 0,
        fontSize: 10,
        fill: "#1e3a5f",
        originX: "center",
        originY: "center",
      });

      // Group rect and label together so they move as one unit
      const group = new fabric.Group([rect, label], {
        left: vol.position.x * SCALE,
        top: vol.position.y * SCALE,
        angle: vol.rotation,
        originX: "center",
        originY: "center",
        selectable: activeTool === "select",
        hasControls: true,
        cornerStyle: "circle",
        cornerColor: VOLUME_STROKE,
      });

      // Mark group as volume for identification
      (group as fabric.Group & { isVolume?: boolean; volumeId?: string }).isVolume = true;
      (group as fabric.Group & { volumeId?: string }).volumeId = vol.id;

      group.on("selected", () => select(vol.id));

      canvas.add(group);
    });

    canvas.renderAll();
  }, [volumes, selectedId, activeTool, getProductById, select]);

  // Handle wall drawing tool
  useEffect(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    let isDrawingLocal = false;
    let startPoint: Point2D | null = null;
    let tempLine: fabric.Line | null = null;

    const snapToGridLocal = (point: Point2D): Point2D => {
      if (!snapToGrid) return point;
      const gridSpacing = gridSize;
      return {
        x: Math.round(point.x / gridSpacing) * gridSpacing,
        y: Math.round(point.y / gridSpacing) * gridSpacing,
      };
    };

    // Convert screen point to canvas coordinates accounting for viewport transform
    const screenToCanvas = (screenPoint: fabric.Point): Point2D => {
      const vpt = canvas.viewportTransform;
      if (!vpt) return { x: screenPoint.x, y: screenPoint.y };
      return {
        x: (screenPoint.x - vpt[4]) / vpt[0],
        y: (screenPoint.y - vpt[5]) / vpt[3],
      };
    };

    if (activeTool === "wall") {
      canvas.selection = false;
      canvas.defaultCursor = "crosshair";

      const handleMouseDown = (e: fabric.TPointerEventInfo) => {
        const evt = e.e as MouseEvent;
        if (evt.altKey || evt.button === 1) return; // Don't draw while panning

        const canvasPoint = screenToCanvas(e.scenePoint);
        const point = snapToGridLocal({
          x: canvasPoint.x / SCALE,
          y: canvasPoint.y / SCALE,
        });
        startPoint = point;
        isDrawingLocal = true;
        setDrawing(true, point);

        tempLine = new fabric.Line(
          [
            point.x * SCALE,
            point.y * SCALE,
            point.x * SCALE,
            point.y * SCALE,
          ],
          {
            stroke: WALL_COLOR,
            strokeWidth: 15 * SCALE,
            strokeLineCap: "round",
            selectable: false,
            evented: false,
          }
        );
        canvas.add(tempLine);
      };

      const handleMouseMove = (e: fabric.TPointerEventInfo) => {
        if (!isDrawingLocal || !tempLine || !startPoint) return;

        const canvasPoint = screenToCanvas(e.scenePoint);
        const endPoint = snapToGridLocal({
          x: canvasPoint.x / SCALE,
          y: canvasPoint.y / SCALE,
        });

        tempLine.set({
          x2: endPoint.x * SCALE,
          y2: endPoint.y * SCALE,
        });
        canvas.renderAll();
      };

      const handleMouseUp = (e: fabric.TPointerEventInfo) => {
        if (!isDrawingLocal || !startPoint) return;

        const canvasPoint = screenToCanvas(e.scenePoint);
        const endPoint = snapToGridLocal({
          x: canvasPoint.x / SCALE,
          y: canvasPoint.y / SCALE,
        });

        // Only add wall if it has some length
        const length = Math.hypot(
          endPoint.x - startPoint.x,
          endPoint.y - startPoint.y
        );
        if (length > 10) {
          addWall(startPoint, endPoint);
        }

        if (tempLine) {
          canvas.remove(tempLine);
          tempLine = null;
        }

        isDrawingLocal = false;
        startPoint = null;
        setDrawing(false);
      };

      canvas.on("mouse:down", handleMouseDown);
      canvas.on("mouse:move", handleMouseMove);
      canvas.on("mouse:up", handleMouseUp);

      return () => {
        canvas.off("mouse:down", handleMouseDown);
        canvas.off("mouse:move", handleMouseMove);
        canvas.off("mouse:up", handleMouseUp);
        canvas.selection = true;
        canvas.defaultCursor = "default";
      };
    } else {
      canvas.selection = activeTool === "select";
      canvas.defaultCursor = "default";
    }
  }, [activeTool, snapToGrid, gridSize, addWall, setDrawing]);

  // Handle door/window placement tool
  useEffect(() => {
    if (!fabricRef.current) return;
    if (activeTool !== "door" && activeTool !== "window") {
      setElementPreview(null);
      return;
    }

    const canvas = fabricRef.current;
    canvas.selection = false;
    canvas.defaultCursor = "crosshair";

    // Convert screen point to canvas coordinates
    const screenToCanvasLocal = (screenPoint: fabric.Point): Point2D => {
      const vpt = canvas.viewportTransform;
      if (!vpt) return { x: screenPoint.x, y: screenPoint.y };
      return {
        x: (screenPoint.x - vpt[4]) / vpt[0],
        y: (screenPoint.y - vpt[5]) / vpt[3],
      };
    };

    // Get element type and dimensions
    const getElementConfig = () => {
      const type: StructuralElementType = activeTool === "door" ? "door" : "window";
      const defaults = ELEMENT_DEFAULTS[type];
      return { type, ...defaults };
    };

    // Handle mouse move for ghost preview
    const handleMouseMove = (e: fabric.TPointerEventInfo) => {
      const canvasPoint = screenToCanvasLocal(e.scenePoint);
      const clickX = canvasPoint.x / SCALE;
      const clickY = canvasPoint.y / SCALE;

      // Detect wall hit
      const hit = detectWallHit(clickX, clickY, walls, 20); // 20cm tolerance

      if (hit) {
        const config = getElementConfig();
        // Validate placement
        const validation = validateElementPlacement(
          hit.wall,
          hit.offset,
          config.width,
          elements
        );

        setElementPreview({
          wallId: hit.wallId,
          offset: hit.offset,
          type: config.type,
          width: config.width,
          isValid: validation.valid,
          reason: validation.reason,
        });
      } else {
        setElementPreview(null);
      }
    };

    // Handle click to place element
    const handleClick = (e: fabric.TPointerEventInfo) => {
      const evt = e.e as MouseEvent;
      // Skip if panning
      if (evt.altKey || evt.button === 1) return;

      const canvasPoint = screenToCanvasLocal(e.scenePoint);
      const clickX = canvasPoint.x / SCALE;
      const clickY = canvasPoint.y / SCALE;

      // Detect wall hit
      const hit = detectWallHit(clickX, clickY, walls, 20);

      if (hit) {
        const config = getElementConfig();
        // Validate placement
        const validation = validateElementPlacement(
          hit.wall,
          hit.offset,
          config.width,
          elements
        );

        if (validation.valid) {
          // Place the element
          addElement(
            hit.wallId,
            config.type,
            hit.offset,
            config.width,
            config.height,
            config.elevation
          );
          // Stay in tool mode for placing more elements
          // User can press Escape or switch tool to exit
        }
      }
    };

    canvas.on("mouse:move", handleMouseMove);
    canvas.on("mouse:down", handleClick);

    return () => {
      canvas.off("mouse:move", handleMouseMove);
      canvas.off("mouse:down", handleClick);
      canvas.selection = true;
      canvas.defaultCursor = "default";
      setElementPreview(null);
    };
  }, [activeTool, walls, elements, addElement]);

  // Handle click to deselect or click-to-place
  useEffect(() => {
    if (!fabricRef.current) return;
    const canvas = fabricRef.current;

    const handleClick = (e: fabric.TPointerEventInfo) => {
      const evt = e.e as MouseEvent;
      // Skip if panning
      if (evt.altKey || evt.button === 1) return;

      // Click-to-place mode: place product when there's pending placement
      if (pendingPlacement && placementMode === "click") {
        const vpt = canvas.viewportTransform;
        let x = e.scenePoint.x;
        let y = e.scenePoint.y;

        if (vpt) {
          x = (x - vpt[4]) / vpt[0];
          y = (y - vpt[5]) / vpt[3];
        }

        // Convert to room coordinates (cm)
        const position = { x: x / SCALE, y: y / SCALE, z: 0 };
        confirmClickPlacement(position);
        return;
      }

      // Normal click-to-deselect behavior
      if (!e.target && activeTool === "select") {
        select(null);
      }
    };

    canvas.on("mouse:down", handleClick);
    return () => {
      canvas.off("mouse:down", handleClick);
    };
  }, [activeTool, select, pendingPlacement, placementMode, confirmClickPlacement]);

  // Handle mouse move for click-to-place preview
  useEffect(() => {
    if (!fabricRef.current) return;
    if (!pendingPlacement || placementMode !== "click") return;

    const canvas = fabricRef.current;

    const handleMouseMove = (e: fabric.TPointerEventInfo) => {
      const vpt = canvas.viewportTransform;
      let x = e.scenePoint.x;
      let y = e.scenePoint.y;

      if (vpt) {
        x = (x - vpt[4]) / vpt[0];
        y = (y - vpt[5]) / vpt[3];
      }

      // Update placement preview
      setPlacementPreview(
        { x: x / SCALE, y: y / SCALE, z: 0 },
        pendingPlacement.productId
      );
    };

    canvas.on("mouse:move", handleMouseMove);
    return () => {
      canvas.off("mouse:move", handleMouseMove);
      clearPlacementPreview();
    };
  }, [pendingPlacement, placementMode, setPlacementPreview, clearPlacementPreview]);

  // Re-render on state changes
  // Note: All draw effects include `room` dependency to re-run after canvas recreation
  useEffect(() => {
    drawGrid();
    drawRoomBoundary();
  }, [room, gridSize, drawGrid, drawRoomBoundary]);

  useEffect(() => {
    drawWalls();
  }, [walls, selectedId, drawWalls, room]);

  useEffect(() => {
    drawElements();
  }, [elements, selectedId, drawElements, room]);

  // Draw element preview when hovering during door/window placement
  useEffect(() => {
    drawElementPreview();
  }, [elementPreview, drawElementPreview]);

  useEffect(() => {
    drawVolumes();
  }, [volumes, selectedId, drawVolumes, room]);

  // BYK-32: Draw snap guides when they change
  useEffect(() => {
    drawSnapGuides();
  }, [snapGuideLines, drawSnapGuides, room]);

  // BYK-3: Keyboard shortcuts for undo/redo and delete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input field
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Undo: Ctrl+Z (or Cmd+Z on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          undo();
        }
        return;
      }

      // Redo: Ctrl+Shift+Z or Ctrl+Y (or Cmd+Shift+Z/Cmd+Y on Mac)
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
        e.preventDefault();
        if (canRedo) {
          redo();
        }
        return;
      }

      // Delete selected item: Delete or Backspace
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
        e.preventDefault();
        // Check if it's a volume (product) and remove it
        const volume = volumes.find((v) => v.id === selectedId);
        if (volume) {
          removeVolume(selectedId);
          select(null);
        }
        return;
      }

      // Cancel placement mode: Escape
      if (e.key === "Escape" && pendingPlacement) {
        e.preventDefault();
        clearPlacementPreview();
        setTool("select");
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canUndo, canRedo, undo, redo, selectedId, volumes, removeVolume, select, pendingPlacement, clearPlacementPreview, setTool]);

  // Convert screen coordinates to canvas coordinates
  const screenToCanvas = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      if (!containerRef.current || !fabricRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      const canvas = fabricRef.current;
      const vpt = canvas.viewportTransform;

      let x = clientX - rect.left;
      let y = clientY - rect.top;

      if (vpt) {
        x = (x - vpt[4]) / vpt[0];
        y = (y - vpt[5]) / vpt[3];
      }

      return { x: x / SCALE, y: y / SCALE };
    },
    []
  );

  // Handle drag and drop with placement validation and enhanced snapping (BYK-32)
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      const productId = e.dataTransfer.types.includes("productid")
        ? e.dataTransfer.getData("productId")
        : null;

      // Calculate position for preview with enhanced snapping
      const pos = screenToCanvas(e.clientX, e.clientY);
      if (pos && productId) {
        // Mark as dragging for visual feedback
        if (!isDragging) {
          setDragging(true);
        }

        // Calculate snapped position
        const snapResult = calculateSnapPosition(
          { x: pos.x, y: pos.y },
          productId,
          null // No current volume (new placement)
        );

        // Use snapped position for preview
        const previewPos = snapResult.didSnap
          ? { x: snapResult.position.x, y: snapResult.position.y, z: 0 }
          : { x: pos.x, y: pos.y, z: 0 };

        setPlacementPreview(previewPos, productId);
        e.dataTransfer.dropEffect = placementPreview.isValid ? "copy" : "none";
      } else {
        e.dataTransfer.dropEffect = "copy";
      }
    },
    [screenToCanvas, setPlacementPreview, placementPreview.isValid, calculateSnapPosition, isDragging, setDragging]
  );

  const handleDragLeave = useCallback(() => {
    clearPlacementPreview();
    clearSnapGuides();
    setDragging(false);
  }, [clearPlacementPreview, clearSnapGuides, setDragging]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const productId = e.dataTransfer.getData("productId");

      const pos = screenToCanvas(e.clientX, e.clientY);
      if (!productId || !pos) {
        clearPlacementPreview();
        clearSnapGuides();
        setDragging(false);
        return;
      }

      // Calculate final snapped position
      const snapResult = calculateSnapPosition(
        { x: pos.x, y: pos.y },
        productId,
        null
      );

      const finalPos = snapResult.didSnap
        ? { x: snapResult.position.x, y: snapResult.position.y, z: 0 }
        : { x: pos.x, y: pos.y, z: 0 };

      // Validate placement before adding
      const validation = isPlacementValid(productId, finalPos);

      if (validation.valid) {
        addVolume(productId, finalPos);
      } else {
        // Show error feedback (could be enhanced with toast notification)
        console.warn("Invalid placement:", validation.reason);
      }

      clearPlacementPreview();
      clearSnapGuides();
      setDragging(false);
      setDraggedProduct(null);
      setTool("select");
    },
    [
      screenToCanvas,
      isPlacementValid,
      addVolume,
      clearPlacementPreview,
      clearSnapGuides,
      setDragging,
      setDraggedProduct,
      setTool,
      calculateSnapPosition,
    ]
  );

  // Get cursor class for current tool or pending placement
  const cursorClass = useMemo(() => {
    // Show crosshair cursor when ready to place via click
    if (pendingPlacement && placementMode === "click") {
      return "cursor-crosshair";
    }
    return TOOL_CURSORS[activeTool] || "cursor-default";
  }, [activeTool, pendingPlacement, placementMode]);

  return (
    <div
      ref={containerRef}
      className={cn("relative h-full w-full overflow-hidden bg-gray-100", cursorClass)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="application"
      aria-label="Kitchen floor plan editor. Tab to select objects, arrow keys to move, R to rotate, Delete to remove."
      tabIndex={0}
    >
      <canvas ref={canvasRef} aria-hidden="true" />

      {/* Zoom controls */}
      <div className="absolute top-4 right-4 flex flex-col gap-1">
        <button
          onClick={() => {
            if (!fabricRef.current) return;
            const newZoom = Math.min(fabricRef.current.getZoom() * 1.2, 3);
            fabricRef.current.setZoom(newZoom);
            setZoom(newZoom);
          }}
          className="rounded bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow hover:bg-gray-50"
          title="Zoom in"
        >
          +
        </button>
        <button
          onClick={() => {
            if (!fabricRef.current) return;
            const newZoom = Math.max(fabricRef.current.getZoom() * 0.8, 0.1);
            fabricRef.current.setZoom(newZoom);
            setZoom(newZoom);
          }}
          className="rounded bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow hover:bg-gray-50"
          title="Zoom out"
        >
          −
        </button>
        <button
          onClick={fitToView}
          className="rounded bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow hover:bg-gray-50"
          title="Fit to view"
        >
          Fit
        </button>
      </div>

      {/* Tool indicator */}
      <div className="absolute bottom-4 left-4 rounded bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow">
        {pendingPlacement && placementMode === "click" && (
          <span className="text-blue-600">Click to place product (ESC to cancel)</span>
        )}
        {!pendingPlacement && activeTool === "wall" && "Drawing walls - click and drag"}
        {!pendingPlacement && activeTool === "select" && "Select - click on objects"}
        {!pendingPlacement && activeTool === "window" && !elementPreview && (
          <span>Place window - hover over a wall</span>
        )}
        {!pendingPlacement && activeTool === "window" && elementPreview && elementPreview.isValid && (
          <span className="text-green-600">Click to place window (ESC to cancel)</span>
        )}
        {!pendingPlacement && activeTool === "window" && elementPreview && !elementPreview.isValid && (
          <span className="text-red-600">Cannot place: {elementPreview.reason}</span>
        )}
        {!pendingPlacement && activeTool === "door" && !elementPreview && (
          <span>Place door - hover over a wall</span>
        )}
        {!pendingPlacement && activeTool === "door" && elementPreview && elementPreview.isValid && (
          <span className="text-green-600">Click to place door (ESC to cancel)</span>
        )}
        {!pendingPlacement && activeTool === "door" && elementPreview && !elementPreview.isValid && (
          <span className="text-red-600">Cannot place: {elementPreview.reason}</span>
        )}
      </div>

      {/* Coordinates & zoom */}
      <div className="absolute bottom-4 right-4 rounded bg-white px-3 py-1.5 text-xs text-gray-500 shadow">
        <div>Room: {room.width}cm x {room.depth}cm</div>
        <div>Zoom: {Math.round(zoom * 100)}%</div>
        <div className="text-gray-400 mt-1">Space+drag or Alt+drag to pan • Scroll to zoom • Ctrl+Z/Y undo/redo</div>
      </div>
    </div>
  );
};

export default FloorPlanCanvas;
